// @ts-check
// Filtros, ordenações e busca da lista na URL (doc 06, seção 14; QueryParamsFiltersEffect do Twenty): um link abre a
// lista já filtrada, sem gravar na view até alguém salvar. Mesmo formato do Twenty:
//   filter[stage][IS][]=VISIT_SCHEDULED&filter[name.lastName][CONTAINS]=Prado&sort[createdAt]=DESC
//   filter[owner][IS][selectedRecordIds][]=<id>&filter[owner][IS][isCurrentWorkspaceMemberSelected]=true
//   filterGroup[operator]=OR&filterGroup[filters][0][field]=city&filterGroup[filters][0][op]=CONTAINS&...
// mais `anyField=<texto>` para a busca em qualquer campo. Módulo puro: recebe os campos por parâmetro.
// `drilldown=f.<receita>` ou `drilldown=s.<receita>` (lista aberta por um clique num gráfico, contrato 07, seção 10) não é
// parâmetro da view: fica na URL quando o rascunho muda e segue para a outra view quando a pessoa troca de view.
import { DRILLDOWN_URL_PARAM } from '../../../../shared/dashboards/drilldown-recipe.js';

/**
 * @typedef {{ id: string, name: string, type: string }} UrlField
 * @typedef {{ id: string, fieldMetadataId: string, operand: string, value: string, subFieldName?: string | null,
 *   viewFilterGroupId?: string | null, positionInViewFilterGroup?: number | null, displayValue?: string }} UrlViewFilter
 * @typedef {{ id: string, parentViewFilterGroupId?: string | null, logicalOperator: string, positionInViewFilterGroup?: number | null }} UrlFilterGroup
 * @typedef {{ id: string, fieldMetadataId: string, direction: string, subFieldName?: string | null }} UrlViewSort
 * @typedef {{ filters: UrlViewFilter[], filterGroups: UrlFilterGroup[], sorts: UrlViewSort[], anyFieldSearch: string }} UrlViewState
 */

export const ANY_FIELD_URL_PARAM = 'anyField';
const VIEW_PARAM_PATTERN = /^(filter|sort|filterGroup)\[/;
const LIST_VALUE_TYPES = new Set(['SELECT', 'MULTI_SELECT']);
const OPERAND_PATTERN = /^[A-Z_]+$/;
// um link com centenas de condições não é de gente: corta para não travar a tela
const MAX_URL_FILTERS = 50;

/** @param {string} key */
export const isViewUrlParam = (key) => key === ANY_FIELD_URL_PARAM || VIEW_PARAM_PATTERN.test(key);

/** @param {URLSearchParams} searchParams */
export const hasViewUrlParams = (searchParams) => [...searchParams.keys()].some(isViewUrlParam);

/**
 * Parâmetros da URL sem os da view (mantém viewId, mock e o resto).
 * @param {URLSearchParams} searchParams
 */
export function stripViewUrlParams(searchParams) {
  const result = new URLSearchParams();
  for (const [key, value] of searchParams) if (!isViewUrlParam(key)) result.append(key, value);
  return result;
}

/** `filter[a][b][]` vira ['filter', 'a', 'b', '']. @param {string} key */
export function splitBracketKey(key) {
  const head = /^[^[]+/.exec(key)?.[0] ?? '';
  const parts = [head];
  const pattern = /\[([^\]]*)\]/g;
  let match;
  while ((match = pattern.exec(key)) !== null) parts.push(match[1]);
  return parts;
}

/**
 * Monta um objeto aninhado a partir das chaves com colchetes; `[]` acumula numa lista.
 * @param {URLSearchParams} searchParams
 * @returns {Record<string, any>}
 */
function toNestedObject(searchParams) {
  /** @type {Record<string, any>} */
  const root = {};
  for (const [key, value] of searchParams) {
    if (!isViewUrlParam(key) || key === ANY_FIELD_URL_PARAM) continue;
    const parts = splitBracketKey(key);
    const isList = parts[parts.length - 1] === '';
    const path = isList ? parts.slice(0, -1) : parts;
    if (path.some((part) => part === '' || part === '__proto__' || part === 'constructor' || part === 'prototype')) continue;
    /** @type {any} */
    let node = root;
    for (const part of path.slice(0, -1)) {
      if (typeof node[part] !== 'object' || node[part] === null || Array.isArray(node[part])) node[part] = {};
      node = node[part];
    }
    const last = /** @type {string} */ (path[path.length - 1]);
    if (isList) {
      if (!Array.isArray(node[last])) node[last] = [];
      // `[]=` vazio é lista vazia, não um item vazio
      if (value !== '') node[last].push(value);
    } else node[last] = value;
  }
  return root;
}

/** @param {unknown} node @returns {any[]} lista ordenada pelas chaves numéricas */
const indexedList = (node) => (node && typeof node === 'object'
  ? (Array.isArray(node) ? node : Object.keys(node).sort((left, right) => Number(left) - Number(right)).map((key) => /** @type {any} */ (node)[key]))
  : []);

/**
 * Lista de textos escrita como JSON (`["A","B"]`); outro texto devolve null.
 * @param {string} text
 * @returns {string[] | null}
 */
function parseJsonList(text) {
  if (!text.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string' || typeof item === 'number') ? parsed.map(String) : null;
  } catch {
    return null;
  }
}

/**
 * Valor de filtro da URL no formato guardado na view: opções viram lista JSON, relações viram { selectedRecordIds }.
 * @param {unknown} urlValue @param {UrlField} field
 */
function toStoredValue(urlValue, field) {
  if (field.type === 'RELATION') {
    if (Array.isArray(urlValue)) return JSON.stringify({ selectedRecordIds: urlValue.map(String) });
    if (urlValue && typeof urlValue === 'object') {
      const object = /** @type {Record<string, any>} */ (urlValue);
      const ids = Array.isArray(object.selectedRecordIds) ? object.selectedRecordIds.map(String) : [];
      return JSON.stringify({ selectedRecordIds: ids, ...(object.isCurrentWorkspaceMemberSelected === 'true' ? { isCurrentWorkspaceMemberSelected: true } : {}) });
    }
    return typeof urlValue === 'string' && urlValue !== '' ? JSON.stringify({ selectedRecordIds: [urlValue] }) : '';
  }
  if (LIST_VALUE_TYPES.has(field.type)) {
    if (Array.isArray(urlValue)) return JSON.stringify(urlValue.map(String));
    // links do Twenty mandam a lista como texto JSON (`filter[stage][IS]=["A","B"]`)
    const jsonList = typeof urlValue === 'string' ? parseJsonList(urlValue) : null;
    if (jsonList) return JSON.stringify(jsonList);
    return typeof urlValue === 'string' && urlValue !== '' ? JSON.stringify([urlValue]) : '';
  }
  if (Array.isArray(urlValue)) return urlValue.map(String).join(',');
  return typeof urlValue === 'string' ? urlValue : '';
}

/**
 * Valor guardado na view no formato da URL (o inverso de toStoredValue).
 * @param {string} storedValue @param {UrlField} field
 * @returns {string | string[] | Record<string, string | string[]>}
 */
function toUrlValue(storedValue, field) {
  if (field.type === 'RELATION' || LIST_VALUE_TYPES.has(field.type)) {
    try {
      const parsed = JSON.parse(storedValue);
      if (Array.isArray(parsed)) return parsed.map(String);
      if (parsed && typeof parsed === 'object') {
        /** @type {Record<string, string | string[]>} */
        const result = { selectedRecordIds: (parsed.selectedRecordIds ?? []).map(String) };
        if (parsed.isCurrentWorkspaceMemberSelected === true) result.isCurrentWorkspaceMemberSelected = 'true';
        return result;
      }
    } catch {
      // valor antigo sem JSON segue como texto
    }
  }
  return storedValue ?? '';
}

/**
 * Pares chave/valor de um valor (texto, lista ou objeto) sob um prefixo.
 * @param {string} prefix @param {string | string[] | Record<string, string | string[]>} value
 * @returns {Array<[string, string]>}
 */
function flattenValue(prefix, value) {
  if (Array.isArray(value)) return value.length === 0 ? [[`${prefix}[]`, '']] : value.map((item) => [`${prefix}[]`, item]);
  if (value && typeof value === 'object') return Object.entries(value).flatMap(([key, item]) => flattenValue(`${prefix}[${key}]`, item));
  return [[prefix, String(value ?? '')]];
}

/**
 * Lê filtros, grupos, ordenações e busca da URL; campos desconhecidos são ignorados. Sem parâmetros da view, null.
 * @param {URLSearchParams} searchParams
 * @param {UrlField[]} fields campos do objeto
 * @param {() => string} createId
 * @returns {UrlViewState | null}
 */
export function parseViewUrlParams(searchParams, fields, createId) {
  if (!hasViewUrlParams(searchParams)) return null;
  const fieldsByName = new Map(fields.map((field) => [field.name, field]));
  const resolve = (/** @type {string} */ path) => {
    const [fieldName, subFieldName] = String(path ?? '').split('.');
    const field = fieldsByName.get(fieldName);
    return field ? { field, subFieldName: subFieldName || null } : null;
  };
  const nested = toNestedObject(searchParams);
  /** @type {UrlViewFilter[]} */
  const filters = [];
  for (const [path, operands] of Object.entries(nested.filter ?? {})) {
    const resolved = resolve(path);
    if (!resolved || !operands || typeof operands !== 'object' || Array.isArray(operands)) continue;
    for (const [operand, value] of Object.entries(operands)) {
      if (!OPERAND_PATTERN.test(operand) || filters.length >= MAX_URL_FILTERS) continue;
      filters.push({ id: createId(), fieldMetadataId: resolved.field.id, operand, value: toStoredValue(value, resolved.field), subFieldName: resolved.subFieldName });
    }
  }
  /** @type {UrlFilterGroup[]} */
  const filterGroups = [];
  /** @param {any} groupNode @param {string | null} parentId @param {number} position @param {number} depth */
  const readGroup = (groupNode, parentId, position, depth) => {
    if (!groupNode || typeof groupNode !== 'object' || depth > 2) return;
    const group = { id: createId(), parentViewFilterGroupId: parentId, logicalOperator: groupNode.operator === 'OR' ? 'OR' : 'AND', positionInViewFilterGroup: position };
    filterGroups.push(group);
    let childPosition = 0;
    for (const item of indexedList(groupNode.filters)) {
      const resolved = resolve(item?.field);
      if (!resolved || !OPERAND_PATTERN.test(String(item?.op ?? '')) || filters.length >= MAX_URL_FILTERS) continue;
      filters.push({
        id: createId(), fieldMetadataId: resolved.field.id, operand: String(item.op), value: toStoredValue(item.value ?? '', resolved.field),
        subFieldName: item.subField || resolved.subFieldName, viewFilterGroupId: group.id, positionInViewFilterGroup: childPosition,
      });
      childPosition += 1;
    }
    for (const child of indexedList(groupNode.groups)) {
      readGroup(child, group.id, childPosition, depth + 1);
      childPosition += 1;
    }
  };
  if (nested.filterGroup) readGroup(nested.filterGroup, null, 0, 1);
  /** @type {UrlViewSort[]} */
  const sorts = [];
  for (const [path, direction] of Object.entries(nested.sort ?? {})) {
    const resolved = resolve(path);
    if (!resolved || typeof direction !== 'string') continue;
    sorts.push({ id: createId(), fieldMetadataId: resolved.field.id, direction: direction.toLowerCase() === 'desc' ? 'desc' : 'asc', subFieldName: resolved.subFieldName });
  }
  return { filters, filterGroups, sorts, anyFieldSearch: (searchParams.get(ANY_FIELD_URL_PARAM) ?? '').slice(0, 100) };
}

/**
 * Pares chave/valor da URL para o estado da lista (o inverso de parseViewUrlParams).
 * @param {UrlViewState} state
 * @param {(fieldMetadataId: string) => UrlField | undefined} getField
 * @returns {Array<[string, string]>}
 */
export function buildViewUrlParams(state, getField) {
  /** @type {Array<[string, string]>} */
  const pairs = [];
  const groupIds = new Set(state.filterGroups.map((group) => group.id));
  const pathOf = (/** @type {UrlField} */ field, /** @type {string | null | undefined} */ subFieldName) => (subFieldName ? `${field.name}.${subFieldName}` : field.name);
  for (const filter of state.filters) {
    if (filter.viewFilterGroupId && groupIds.has(filter.viewFilterGroupId)) continue;
    const field = getField(filter.fieldMetadataId);
    if (!field) continue;
    pairs.push(...flattenValue(`filter[${pathOf(field, filter.subFieldName)}][${filter.operand}]`, toUrlValue(filter.value, field)));
  }
  const root = state.filterGroups.find((group) => !group.parentViewFilterGroupId);
  /** @param {UrlFilterGroup} group @param {string} prefix */
  const writeGroup = (group, prefix) => {
    pairs.push([`${prefix}[operator]`, group.logicalOperator === 'OR' ? 'OR' : 'AND']);
    const rules = state.filters
      .filter((filter) => filter.viewFilterGroupId === group.id)
      .sort((left, right) => (left.positionInViewFilterGroup ?? 0) - (right.positionInViewFilterGroup ?? 0));
    rules.forEach((filter, index) => {
      const field = getField(filter.fieldMetadataId);
      if (!field) return;
      pairs.push([`${prefix}[filters][${index}][field]`, field.name], [`${prefix}[filters][${index}][op]`, filter.operand]);
      if (filter.subFieldName) pairs.push([`${prefix}[filters][${index}][subField]`, filter.subFieldName]);
      pairs.push(...flattenValue(`${prefix}[filters][${index}][value]`, toUrlValue(filter.value, field)));
    });
    state.filterGroups
      .filter((child) => child.parentViewFilterGroupId === group.id)
      .sort((left, right) => (left.positionInViewFilterGroup ?? 0) - (right.positionInViewFilterGroup ?? 0))
      .forEach((child, index) => writeGroup(child, `${prefix}[groups][${index}]`));
  };
  if (root) writeGroup(root, 'filterGroup');
  for (const sort of state.sorts) {
    const field = getField(sort.fieldMetadataId);
    if (field) pairs.push([`sort[${pathOf(field, sort.subFieldName)}]`, String(sort.direction).toLowerCase() === 'desc' ? 'DESC' : 'ASC']);
  }
  if (state.anyFieldSearch.trim() !== '') pairs.push([ANY_FIELD_URL_PARAM, state.anyFieldSearch.trim()]);
  return pairs;
}

/**
 * Busca da URL ao escolher outra view no seletor: na lista aberta por um clique num gráfico, o recorte (drilldown,
 * filtros e ordenação da URL) segue para a outra view; fora dela, só a view.
 * @param {string} currentSearch @param {string} viewId
 */
export function buildViewSwitchSearch(currentSearch, viewId) {
  const current = new URLSearchParams(currentSearch);
  const result = new URLSearchParams({ viewId });
  const drilldown = current.get(DRILLDOWN_URL_PARAM);
  if (drilldown) {
    result.set(DRILLDOWN_URL_PARAM, drilldown);
    for (const [key, value] of current) if (isViewUrlParam(key)) result.append(key, value);
  }
  return `?${result.toString()}`;
}

/**
 * Busca da URL sem o recorte de um clique num gráfico (o drilldown e os parâmetros da view que ele trouxe).
 * @param {string} currentSearch
 */
export function stripDrilldownUrlParams(currentSearch) {
  const result = stripViewUrlParams(new URLSearchParams(currentSearch));
  result.delete(DRILLDOWN_URL_PARAM);
  const text = result.toString();
  return text ? `?${text}` : '';
}

/**
 * Busca da URL com os parâmetros da view trocados pelos pares dados (mantém viewId e os outros).
 * @param {string} currentSearch @param {Array<[string, string]>} pairs
 */
export function replaceViewUrlParams(currentSearch, pairs) {
  const result = stripViewUrlParams(new URLSearchParams(currentSearch));
  for (const [key, value] of pairs) result.append(key, value);
  const text = result.toString();
  return text ? `?${text}` : '';
}
